import React, {useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import './index.scss';
import Input from '../../components/Input';
import Select from 'react-select';
import {
  useGetEquipmentTypeQuery,
  useLazyGetEquipmentQuery,
  useLazyGetFacilityQuery,
} from '../../store/builder/inspections.api';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useCallback, useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {useDebounce} from '../LogSheets/hooks';
import useLoader from '../../global/UseLoadingHandler';
import EquipmentActiveTab from './components/Tabs/EquipmentActiveTab';
import EquipmentTabHeader from './components/EquipmentTabHeader';
import EquipmentDecommissionedTab from './components/Tabs/EquipmentDecommissionedTab';
import EquipmentArchivedTab from './components/Tabs/EquipmentArchivedTab';
import EquipmentTransfersTab from './components/Tabs/EquipmentTransfersTab';
import EquipmentTransferModal from './components/EquipmentTransferModal/EquipmentTransferModal';
import UnAssignModal from './components/UnAssignModal/UnAssignModal';
import DeleteEquipmentModal from './components/DeleteEquipmentModal/DeleteEquipmentModal';
import ActivateEquipmentModal from './components/ActivateEquipmentModal/ActivateEquipmentModal.jsx';
import ArchiveEquipmentModal from './components/ArchiveEquipmentModal/ArchiveEquipmentModal.jsx';
import DecommissionEquipmentModal from './components/DecommissionEquipmentModal/DecommissionEquipmentModal.jsx';
import UnArchiveEquipmentModal from './components/UnArchiveEquipmentModal/UnArchiveEquipmentModal.jsx';
import {useTranslation} from '../../hooks/useTranslation';
import { useSelector } from 'react-redux';

export default function EquipmentManagement() {
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get('active') || '0';
  const equipment_type = searchParams.get('equipment_type');
  const facility = searchParams.get('facility');
  const search = searchParams.get('search');
  const pageTab = +active;
  const [currentTab, setCurrentTab] = useState(
    pageTab && +pageTab !== '' ? +pageTab : 0,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilter, setSearchFilter] = useState(
    search ? search : '',
  );
  const [currentId, setCurrentId] = useState(null);
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [filters, setFilters] = useState({
    equipment_type_id: null,
    facility_id: null,
  });
  const debouncedSearchValue = useDebounce(searchFilter, 500);
  // Modals
  const [transferModal, setTransferModal] = useState(false);
  const [unAssignStatus, setUnAssignStatus] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [activateModal, setActivateModal] = useState(false);
  const [archiveModal, setArchiveModal] = useState(false);
  const [decommissionModal, setDecommissionModal] = useState(false);
  const [unarchiveModal, setUnarchiveModal] = useState(false);


  const changeTab = tabId => {
    setCurrentTab(+tabId);
    
    // Update URL with new tab
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('active', tabId);
    setSearchParams(newSearchParams);
  };

  const [
    getEquipment,
    {
      data: equipmentData,
      isFetching: equipmentIsLoading,
    },
  ] = useLazyGetEquipmentQuery();

  const [
    getFacility,
    {
      data: facilityData,
      isSuccess: facilityIsSuccess,
    },
  ] = useLazyGetFacilityQuery();  

  const {
    data: equipmentTypeData,
    isSuccess: equipmentTypeIsSuccess,
  } = useGetEquipmentTypeQuery();

  const getEquipmentsInitial = useCallback(() => {
    const queryParams = {
      page: currentPage,
      equipment_type_id: filters.equipment_type_id || null,
      facility_id: filters.facility_id || null,
      search: debouncedSearchValue || null,
    };

    // Send active only for first and second tabs
    if (pageTab === 0) {
      queryParams.active = 'true';
    } else if (pageTab === 1) {
      queryParams.active = 'false';
    }

    // Send archived only for third tab
    if (pageTab === 2) {
      queryParams.archived = 'true';
    }

    // Send transfers only for fourth tab
    if (pageTab === 3) {
      queryParams.transfers = 'true';
    }

    getEquipment(queryParams);
  }, [pageTab, filters, debouncedSearchValue, getEquipment]);

  useEffect(() => {
    getFacility();
  }, [
    getFacility,
  ]);

  // Sync currentTab with URL when component mounts or URL changes
  useEffect(() => {
    setCurrentTab(pageTab);
  }, [pageTab]);

  useEffect(() => {
    setFilters(old => ({
      ...old,
      equipment_type_id: equipment_type ? equipment_type : null,
      facility_id: facility ? facility : null,
    }));
  }, [
    equipment_type,
    facility,
    setFilters,
  ]);

  useEffect(() => {
    getEquipmentsInitial();
  }, [
    filters,
    debouncedSearchValue,
    pageTab,
    getEquipment,
    getEquipmentsInitial,
  ]);

  useLoader([
    equipmentIsLoading,
  ]);

  useEffect(() => {
    if (equipmentTypeIsSuccess) {
      setEquipmentTypeOptions(
        equipmentTypeData?.map(item => ({
          value: item.id,
          label: item.title,
        })),
      );
    }
    if (facilityIsSuccess) {
      setFacilityOptions(
        facilityData?.map(item => ({
          value: item.id,
          label: item.name,
        })),
      );
    }
  }, [
    equipmentTypeIsSuccess,
    facilityIsSuccess,
    equipmentTypeData,
    facilityData,
  ]);

  const handleSearch = e => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (e.target.value !== '') {
      newSearchParams.set('search', e.target.value);
    } else {
      newSearchParams.delete('search');
    }
    setSearchParams(newSearchParams);
    setSearchFilter(e.target.value);
    handlePageClick(1);
  };

  const handlePageClick = page => {
    let body = {
      page: page.selected + 1,
      equipment_type_id: filters.equipment_type_id
        ? filters.equipment_type_id
        : null,
      facility_id: filters.facility_id ? filters.facility_id : null,
      search: debouncedSearchValue ? debouncedSearchValue : null,
    };

    // Send active only for first and second tabs
    if (pageTab === 0) {
      body.active = 'true';
    } else if (pageTab === 1) {
      body.active = 'false';
    }

    // Send archived only for third tab
    if (pageTab === 2) {
      body.archived = 'true';
    }

    // Send transfers only for fourth tab
    if (pageTab === 3) {
      body.transfers = 'true';
    }

    getEquipment(body);
    setCurrentPage(page.selected + 1);
    window.scrollTo(0, 0);
  };

  const handleTransfer = (equipment) => {
    setTransferModal(true);
    setCurrentId(equipment?.id);
  };

  const handleUnAssign = (equipment) => {
    setUnAssignStatus(true);
    setCurrentId(equipment?.id);
  };

  const handleDelete = (equipment) => {
    setDeleteModal(true);
    setCurrentId(equipment?.id);
  };

  const handleActivate = (equipment) => {
    setActivateModal(true);
    setCurrentId(equipment?.id);
  };

  const handleArchive = (equipment) => {
    setArchiveModal(true);
    setCurrentId(equipment?.id);
  };

  const handleDecommission = (equipment) => {
    setDecommissionModal(true);
    setCurrentId(equipment?.id);
  };

  const handleUnarchive = (equipment) => {
    setUnarchiveModal(true);
    setCurrentId(equipment?.id);
  };

  return (
    <>
      <Wrapper>
        <Helmet>
          <title>{t('equipmentManagement.pageTitle')}</title>
        </Helmet>
        <DashboardHeader
          title={t('equipmentManagement.title')}
          showBtn={true}
          action={() => navigate('/create-equipment')}
          type={'equipment'}
        />
        <div className={`equipment`}>
          <EquipmentTabHeader
            currentTab={currentTab}
            changeTab={changeTab}
            setCurrentPage={setCurrentPage}
            getEquipment={getEquipment}
            filters={filters}
            debouncedSearchValue={debouncedSearchValue}
          />
          <div className='equipment__filters'>
                <Input
                  defaultValue={search}
                  placeholder={t('equipmentManagement.searchPlaceholder')}
                  maxwidth={200}
                  onChange={handleSearch}
                />
                <Select
                  key={JSON.stringify(facilityOptions)}
                  defaultValue={
                    facilityOptions?.filter(
                      item => item?.value === facility,
                    )[0]
                  }
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                      borderRadius: 8,
                      height: 42,
                      backgroundColor: '#F4F5F6',
                      marginTop: 8,

                    }),
                  }}
                  isClearable
                  options={facilityOptions}
                  onChange={e => {
                    setCurrentPage(1);
                    const newSearchParams = new URLSearchParams(searchParams);
                    if (e?.value) {
                      newSearchParams.set('facility', e.value);
                    } else {
                      newSearchParams.delete('facility');
                    }
                    setSearchParams(newSearchParams);
                    setFilters(old => ({...old, facility_id: e?.value}));
                  }}
                  placeholder={t('equipmentManagement.facilityPlaceholder')}
                />

                <Select
                  key={equipmentTypeOptions}
                  defaultValue={
                    equipmentTypeOptions?.filter(
                      item => item?.value === equipment_type,
                    )[0]
                  }
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                      borderRadius: 8,
                      height: 42,
                      backgroundColor: '#F4F5F6',
                      marginTop: 8,

                    }),
                  }}
                  isClearable
                  options={equipmentTypeOptions}
                  placeholder={t('equipmentManagement.equipmentTypePlaceholder')}
                  onChange={e => {
                    setCurrentPage(1);
                    const newSearchParams = new URLSearchParams(searchParams);
                    if (e?.value) {
                      newSearchParams.set('equipment_type', e.value);
                    } else {
                      newSearchParams.delete('equipment_type');
                    }
                    setSearchParams(newSearchParams);
                    setFilters(old => ({
                      ...old,
                      equipment_type_id: e?.value,
                    }));
                  }}
            />
          </div>
          {currentTab === 0 && (
            <EquipmentActiveTab
              equipmentData={equipmentData}
              setCurrentId={setCurrentId}
              currentId={currentId}
              handlePageClick={handlePageClick}
              currentPage={currentPage}
              handleTransfer={handleTransfer}
              handleUnAssign={handleUnAssign}
              handleDelete={handleDelete}
              handleActivate={handleActivate}
              handleArchive={handleArchive}
              handleDecommission={handleDecommission}
            />
          )}
          
          {currentTab === 1 && (
            <EquipmentDecommissionedTab
              equipmentData={equipmentData}
              setCurrentId={setCurrentId}
              currentId={currentId}
              handlePageClick={handlePageClick}
              currentPage={currentPage}
              handleTransfer={handleTransfer}
              handleUnAssign={handleUnAssign}
              handleActivate={handleActivate}
              handleArchive={handleArchive}
              handleDecommission={handleDecommission}
            />
          )}
          
          {currentTab === 2 && (
            <EquipmentArchivedTab
              equipmentData={equipmentData}
              setCurrentId={setCurrentId}
              currentId={currentId}
              handlePageClick={handlePageClick}
              currentPage={currentPage}
              handleUnAssign={handleUnAssign}
              handleDelete={handleDelete}
              handleActivate={handleActivate}
              handleArchive={handleArchive}
              handleUnarchive={handleUnarchive}
              handleDecommission={handleDecommission}
            />
          )}
          {currentTab === 3 && userInfo?.is_corporate && (
            <EquipmentTransfersTab
              equipmentData={equipmentData}
              getEquipment={getEquipment}
              getEquipmentsInitial={getEquipmentsInitial}
              setCurrentId={setCurrentId}
              currentId={currentId}
              pageTab={pageTab}
              filters={filters}
              debouncedSearchValue={debouncedSearchValue}
              currentTab={currentTab}
              handlePageClick={handlePageClick}
              currentPage={currentPage}
              handleUnAssign={handleUnAssign}
              handleDelete={handleDelete}
              handleActivate={handleActivate}
              handleArchive={handleArchive}
              handleDecommission={handleDecommission}
            />
          )}
        </div>
      </Wrapper>

      {/* Modals */}
      {transferModal && <EquipmentTransferModal
        equipment={equipmentData?.data?.find(el => el?.id === currentId)}
        isOpen={transferModal}
        setIsOpen={setTransferModal}
        getEquipmentsInitial={getEquipmentsInitial}
      />}
      {unAssignStatus && <UnAssignModal
        equipment={equipmentData?.data?.find(el => el?.id === currentId)}
        setIsOpen={setUnAssignStatus}
        getEquipmentsInitial={getEquipmentsInitial}
      />}
      {deleteModal && <DeleteEquipmentModal
        equipment={equipmentData?.data?.find(el => el?.id === currentId)}
        setIsOpen={setDeleteModal}
        getEquipmentsInitial={getEquipmentsInitial}
      />} 
      {activateModal && <ActivateEquipmentModal
        equipment={equipmentData?.data?.find(el => el?.id === currentId)}
        setIsOpen={setActivateModal}
        getEquipmentsInitial={getEquipmentsInitial}
      />}
      {archiveModal && <ArchiveEquipmentModal
        equipment={equipmentData?.data?.find(el => el?.id === currentId)}
        setIsOpen={setArchiveModal}
        getEquipmentsInitial={getEquipmentsInitial}
      />}
      {decommissionModal && <DecommissionEquipmentModal
        equipment={equipmentData?.data?.find(el => el?.id === currentId)}
        setIsOpen={setDecommissionModal}
        getEquipmentsInitial={getEquipmentsInitial}
      />}
      {unarchiveModal && <UnArchiveEquipmentModal
        equipment={equipmentData?.data?.find(el => el?.id === currentId)}
        setIsOpen={setUnarchiveModal}
        getEquipmentsInitial={getEquipmentsInitial}
      />}
      
    </>
  );
}
